import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import { Link } from "react-router-dom";
import { ChevronRight, PencilLine, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { Button } from "../../components/common/Button";
import { InfoBar } from "../../components/common/InfoBar";
import { Input } from "../../components/common/Input";
import "react-quill/dist/quill.snow.css";
import "./styles.css";

type CooperationModelData = {
  name: string;
  description: string;
};

type CooperationModelRecord = {
  id: number | string;
  created_at?: string;
  model_data?: Partial<CooperationModelData> | null;
};

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, "");
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

const EMPTY_FORM: CooperationModelData = {
  name: "",
  description: "",
};

const DEFAULT_DESCRIPTION_TEMPLATE =
  "<p>Поддержка со стороны агентства:</p><ul><li>Чат поддержки</li><li>Наставник по сложным вопросам</li><li>Онлайн-встречи</li></ul>";

const QUILL_MODULES = {
  toolbar: [
    ["bold"],
    [{ size: ["large", false, "small"] }],
    [{ list: "bullet" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["bold", "size", "list", "bullet"];

const normalizeRecord = (record: CooperationModelRecord) => ({
  ...record,
  model_data: {
    name: record?.model_data?.name?.trim() || "",
    description: record?.model_data?.description?.trim() || "",
  },
});

const getHeaders = () => ({
  apikey: SUPABASE_ANON_KEY as string,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

const formatCreatedAt = (value?: string) => {
  if (!value) return "Без даты";

  return new Date(value).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeHtml = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed || trimmed === "<p><br></p>") {
    return "";
  }

  return trimmed;
};

export const CooperationModelsEditor = () => {
  const [models, setModels] = useState<CooperationModelRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | string | "new">("new");
  const [form, setForm] = useState<CooperationModelData>(EMPTY_FORM);
  const [pending, setPending] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hasSupabaseConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

  const fetchModels = useCallback(async () => {
    if (!hasSupabaseConfig) {
      setPending(false);
      setModels([]);
      setErrorMessage(null);
      return;
    }

    setPending(true);
    setErrorMessage(null);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/cooperation_models?select=id,created_at,model_data&order=created_at.asc`,
        {
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error("Не удалось загрузить модели.");
      }

      const payload = ((await response.json()) as CooperationModelRecord[]).map(
        normalizeRecord,
      );

      setModels(payload);
      setSelectedId((currentId) => {
        if (currentId === "new") return "new";
        return payload.some((item) => item.id === currentId) ? currentId : "new";
      });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось загрузить модели.",
      );
    } finally {
      setPending(false);
    }
  }, [hasSupabaseConfig]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const selectedModel = useMemo(
    () => models.find((item) => item.id === selectedId) ?? null,
    [models, selectedId],
  );

  useEffect(() => {
    if (selectedModel) {
      setForm({
        name: selectedModel.model_data?.name || "",
        description: selectedModel.model_data?.description || "",
      });
      return;
    }

    if (selectedId === "new") {
      setForm(EMPTY_FORM);
    }
  }, [selectedId, selectedModel]);

  const setField = (field: keyof CooperationModelData, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleCreate = () => {
    resetMessages();
    setSelectedId("new");
    setForm(EMPTY_FORM);
  };

  const handleSelect = (id: number | string) => {
    resetMessages();
    setSelectedId(id);
  };

  const handleSave = async () => {
    if (!hasSupabaseConfig) return;

    const trimmedName = form.name.trim();
    const normalizedDescription = normalizeHtml(form.description);

    if (!trimmedName) {
      setErrorMessage("Заполни название модели.");
      return;
    }

    setSaving(true);
    resetMessages();

    try {
      const payload = {
        model_data: {
          name: trimmedName,
          description: normalizedDescription,
        },
      };

      const isCreate = selectedId === "new";
      const targetUrl = isCreate
        ? `${SUPABASE_URL}/rest/v1/cooperation_models`
        : `${SUPABASE_URL}/rest/v1/cooperation_models?id=eq.${selectedId}`;

      const response = await fetch(targetUrl, {
        method: isCreate ? "POST" : "PATCH",
        headers: {
          ...getHeaders(),
          Prefer: "return=representation",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          isCreate
            ? "Не удалось создать модель."
            : "Не удалось сохранить изменения.",
        );
      }

      const saved = ((await response.json()) as CooperationModelRecord[]).map(
        normalizeRecord,
      );

      const savedModel = saved[0];
      if (!savedModel) {
        throw new Error("Supabase не вернул сохранённую запись.");
      }

      setModels((current) => {
        if (isCreate) {
          return [...current, savedModel].sort((a, b) => {
            const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
            return aDate - bDate;
          });
        }

        return current.map((item) => (item.id === savedModel.id ? savedModel : item));
      });

      setSelectedId(savedModel.id);
      setSuccessMessage(isCreate ? "Модель создана." : "Изменения сохранены.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось сохранить модель.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!hasSupabaseConfig || selectedId === "new") return;

    setDeleting(true);
    resetMessages();

    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/cooperation_models?id=eq.${selectedId}`,
        {
          method: "DELETE",
          headers: getHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error("Не удалось удалить модель.");
      }

      setModels((current) => current.filter((item) => item.id !== selectedId));
      setSelectedId("new");
      setForm(EMPTY_FORM);
      setSuccessMessage("Модель удалена.");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Не удалось удалить модель.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="cooperation-editor">
      <div className="cooperation-editor__container">
        <Link className="cooperation-editor__back-link" to="/cooperation-models">
          <ChevronRight size={16} />
          К витрине моделей
        </Link>

        <div className="cooperation-editor__header">
          <div>
            <span className="cooperation-editor__eyebrow">Скрытый редактор</span>
            <h1>Редактор моделей сотрудничества</h1>
            <p>
              Временный анонимный CRUD для внутренней работы. Описание редактируется
              через WYSIWYG-редактор и сохраняется в `model_data.description` как HTML.
            </p>
          </div>

          <div className="cooperation-editor__header-actions">
            <Button
              iconLeft={<RefreshCw size={16} />}
              onClick={fetchModels}
              text="Обновить"
              variant="secondary"
            />
            <Button
              iconLeft={<Plus size={16} />}
              onClick={handleCreate}
              text="Новая модель"
            />
          </div>
        </div>

        {!hasSupabaseConfig && (
          <InfoBar
            className="cooperation-editor__info"
            type="warning"
            text="Не найдены REACT_APP_SUPABASE_URL и REACT_APP_SUPABASE_ANON_KEY."
          />
        )}

        {errorMessage && (
          <InfoBar
            className="cooperation-editor__info cooperation-editor__info--error"
            type="error"
            text={errorMessage}
          />
        )}

        {successMessage && (
          <InfoBar
            className="cooperation-editor__info cooperation-editor__info--success"
            type="success"
            text={successMessage}
          />
        )}

        <div className="cooperation-editor__layout">
          <aside className="cooperation-editor__sidebar">
            <div className="cooperation-editor__sidebar-header">
              <div>
                <strong>Список моделей</strong>
                <span>{models.length} записей</span>
              </div>
            </div>

            <div className="cooperation-editor__items">
              <button
                className={[
                  "cooperation-editor__item",
                  selectedId === "new" ? "cooperation-editor__item--active" : "",
                ].join(" ")}
                onClick={handleCreate}
                type="button"
              >
                <div className="cooperation-editor__item-icon">
                  <Plus size={16} />
                </div>
                <div>
                  <strong>Новая модель</strong>
                  <span>Создать новую запись</span>
                </div>
              </button>

              {pending ? (
                <div className="cooperation-editor__empty">Загрузка списка...</div>
              ) : null}

              {!pending && !models.length ? (
                <div className="cooperation-editor__empty">
                  Пока нет записей. Начни с первой модели.
                </div>
              ) : null}

              {models.map((model) => (
                <button
                  className={[
                    "cooperation-editor__item",
                    selectedId === model.id ? "cooperation-editor__item--active" : "",
                  ].join(" ")}
                  key={model.id}
                  onClick={() => handleSelect(model.id)}
                  type="button"
                >
                  <div className="cooperation-editor__item-icon">
                    <PencilLine size={16} />
                  </div>
                  <div>
                    <strong>{model.model_data?.name || "Без названия"}</strong>
                    <span>{formatCreatedAt(model.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          <section className="cooperation-editor__panel">
            <div className="cooperation-editor__form">
              <div className="cooperation-editor__section-head">
                <div>
                  <h2>{selectedId === "new" ? "Новая модель" : "Редактирование"}</h2>
                  <p>
                    Поле названия лучше хранить как короткий ratio: `80/20`, `70/30`,
                    `60/40`.
                  </p>
                </div>
              </div>

              <div className="cooperation-editor__field">
                <Input
                  label="Название модели"
                  onChange={(value) => setField("name", value)}
                  placeholder="Например, 80/20"
                  value={form.name}
                />
              </div>

              <div className="cooperation-editor__field">
                <div className="textarea_label">Описание</div>

                <div className="cooperation-editor__quick-actions">
                  <button
                    className="cooperation-editor__secondary-action"
                    onClick={() =>
                      setField(
                        "description",
                        normalizeHtml(form.description) || DEFAULT_DESCRIPTION_TEMPLATE,
                      )
                    }
                    type="button"
                  >
                    Подставить шаблон
                  </button>
                </div>

                <div className="cooperation-editor__quill">
                  <ReactQuill
                    formats={QUILL_FORMATS}
                    modules={QUILL_MODULES}
                    onChange={(value) => setField("description", value)}
                    placeholder="Опиши модель обычным текстом или списком"
                    theme="snow"
                    value={form.description}
                  />
                </div>
              </div>

              <div className="cooperation-editor__actions">
                <Button
                  iconLeft={<Save size={16} />}
                  onClick={handleSave}
                  pending={saving}
                  text={selectedId === "new" ? "Создать" : "Сохранить"}
                />
                {selectedId !== "new" && (
                  <Button
                    iconLeft={<Trash2 size={16} />}
                    onClick={handleDelete}
                    pending={deleting}
                    text="Удалить"
                    tone="danger"
                    variant="secondary"
                  />
                )}
              </div>
            </div>

            <div className="cooperation-editor__preview">
              <div className="cooperation-editor__preview-label">Предпросмотр</div>
              <div className="cooperation-editor__preview-card">
                <div className="cooperation-editor__pin" />
                <div className="cooperation-editor__preview-index">
                  {selectedId === "new" ? "Новая карточка" : `ID ${selectedId}`}
                </div>
                <h3>{form.name.trim() || "80/20"}</h3>

                {normalizeHtml(form.description) ? (
                  <div
                    className="cooperation-editor__preview-rich"
                    dangerouslySetInnerHTML={{ __html: form.description }}
                  />
                ) : (
                  <p>
                    Здесь появится структура карточки. Можно писать абзацами или
                    списком.
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
